/**
 * Attendance API — the attempt log.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-31-1256-feat-attendance-attempt-log-plan.md (U2, U3).
 *
 * `GET /v1/schools/:school_public_id/attendance-attempts` — SUPER-ADMIN ONLY
 * (R8). The rows describe absence records for named minors, so the read is
 * gated the way the moderation audit trail is and the access is itself audited.
 */

import { z } from 'zod'
import {
  AttendanceAttemptPersonSchema,
  AttendanceAttemptSchema,
  AttendanceAttemptSectionSchema
} from '../../schemas/entities/attendance-attempt'

/** Never serve an unbounded page of a table with no retention story. */
export const ATTENDANCE_ATTEMPTS_MAX_LIMIT = 200
export const ATTENDANCE_ATTEMPTS_DEFAULT_LIMIT = 50

export const ListAttendanceAttemptsRequestSchema = z.object({
  /** `School.publicId` — the attempts are always scoped to one school (R7). */
  school_public_id: z.string().min(1),
  limit: z.coerce.number().min(1).max(ATTENDANCE_ATTEMPTS_MAX_LIMIT).optional(),
  offset: z.coerce.number().min(0).optional()
})

export type ListAttendanceAttemptsRequest = z.infer<typeof ListAttendanceAttemptsRequestSchema>

export const ListAttendanceAttemptsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    /** Newest first (R9). */
    attempts: z.array(AttendanceAttemptSchema),
    total: z.number(),
    /**
     * Every person this page names, keyed by `UsersOnSchools.publicId` — the id
     * the rows store for students, calls, resolution errors and overrides.
     * Optional so a response from before the lookup maps existed still parses.
     */
    people: z.record(z.string(), AttendanceAttemptPersonSchema).optional(),
    /**
     * Every section this page names, keyed by BOTH its Blackbaud platform id
     * (what a parent-lane call stores) and its `Section.publicId` (what a
     * teacher row stores), so either reference resolves with one lookup.
     */
    sections: z.record(z.string(), AttendanceAttemptSectionSchema).optional()
  })
})

export type ListAttendanceAttemptsResponse = z.infer<typeof ListAttendanceAttemptsResponseSchema>
